import {useCallback, useEffect, useState} from "react";
import {dibAppApi} from "../../../dibAppApi/dibAppApi";
import type {RenderableDocument} from "../../content/types";
import type {SourceInfo} from "../../../types";

export type CitationPartsStatus =
    | "idle"
    | "loading"
    | "loaded"
    | "unavailable"
    | "denied";

export type CitationPartsResult = {
    status: CitationPartsStatus;
    parts: RenderableDocument[] | null;
};

export type ParsedDibUrl = {
    topicId?: string;
    segmentId?: string;
    sectionId?: string;
};

/** The place to fetch parts from: a topic, an optional segment and section. */
export type CitationTarget = {
    resourceId?: string;
    segmentId?: string;
    sectionId?: string;
};

/** A lazy trigger refetches by default. Serve a cached excerpt instead. */
const PREFER_CACHE = true;

/** Prefetches run at most this many at a time, so a fast scroll through a
 *  long thread does not send too many requests to the API or delay chat requests. */
const MAX_PREFETCH_IN_FLIGHT = 2;

let prefetchInFlight = 0;
const prefetchQueue: Array<() => Promise<unknown>> = [];

function pumpPrefetchQueue() {
    while (prefetchInFlight < MAX_PREFETCH_IN_FLIGHT && prefetchQueue.length > 0) {
        const run = prefetchQueue.shift()!;
        prefetchInFlight += 1;
        run().finally(() => {
            prefetchInFlight -= 1;
            pumpPrefetchQueue();
        });
    }
}

/** Matches the "/d/<topicId>" segment, with an optional "/segment/<id>" after it. */
const DIB_URL_PATTERN = /\/d\/([^/#?]+)(?:\/segment\/([^/#?]+))?/;

/**
 * Parses a dib url, relative or absolute, into its topic, segment and
 * section ids. The shape is "<topicTypeSlug>/d/<topicId>
 * [/segment/<segmentId>][#<sectionId>]". A legacy file download url
 * (dibapi/document/file) carries no topic id and is not previewable.
 */
export function parseDibUrl(url: string | null | undefined): ParsedDibUrl {
    if (!url) return {};

    const hashIndex = url.indexOf("#");
    const path = hashIndex === -1 ? url : url.slice(0, hashIndex);
    const rawSectionId = hashIndex === -1 ? "" : url.slice(hashIndex + 1).trim();
    const sectionId = rawSectionId.length > 0 ? rawSectionId : undefined;

    if (path.includes("dibapi/document/file")) {
        return {};
    }

    const match = path.match(DIB_URL_PATTERN);
    if (!match) return {};

    return {
        topicId: match[1],
        segmentId: match[2],
        sectionId,
    };
}

/**
 * Resolves the cited target for a source.
 *
 * A source's `id` is only the topic id for sources that arrived this
 * session over SSE. History-loaded sources set `id` to a Redis key
 * instead, the url-escaped relative url, so the topic id must come from
 * `source.topicId` or be parsed out of `source.url`.
 */
export function targetFromSource(source: SourceInfo | null): CitationTarget | null {
    if (!source) return null;

    const parsed = parseDibUrl(source.url);
    return {
        resourceId: source.topicId ?? parsed.topicId,
        segmentId: source.segmentId || parsed.segmentId || "",
        sectionId: parsed.sectionId,
    };
}

/** True when two targets fetch the same content. */
export function sameTarget(
    a: CitationTarget | null,
    b: CitationTarget | null
): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    return (
        (a.resourceId ?? "") === (b.resourceId ?? "") &&
        (a.segmentId ?? "") === (b.segmentId ?? "") &&
        (a.sectionId ?? "") === (b.sectionId ?? "")
    );
}

/**
 * Starts a prefetch of a target's parts without waiting for it to finish.
 * Warms the RTK Query cache so the preview panel can open with the parts
 * already fetched.
 */
export function usePrefetchCitationParts(): (target: CitationTarget | null) => void {
    const [trigger] = dibAppApi.endpoints.getDocumentParts.useLazyQuery();

    return useCallback(
        (target: CitationTarget | null) => {
            if (!target?.resourceId) return;
            if (typeof document !== "undefined" && document.visibilityState === "hidden") {
                return;
            }
            const args = {
                resourceId: target.resourceId,
                segmentId: target.segmentId ?? "",
                id: target.sectionId ?? "",
            };
            prefetchQueue.push(() =>
                Promise.resolve(trigger(args, PREFER_CACHE)).catch(() => undefined)
            );
            pumpPrefetchQueue();
        },
        [trigger]
    );
}

/** True when an RTK Query error is an HTTP 401 or 403. */
function isAccessDenied(error: unknown): boolean {
    if (!error || typeof error !== "object") return false;
    const status = (error as {status?: unknown}).status;
    return status === 401 || status === 403;
}

/**
 * Loads DIB parts for a target, for the preview box.
 * Tries the section id first when the target carries one. Falls back once
 * to the segment level when that call fails or is empty.
 */
export function useCitationParts(target: CitationTarget | null): CitationPartsResult {
    const [trigger] = dibAppApi.endpoints.getDocumentParts.useLazyQuery();
    const [result, setResult] = useState<CitationPartsResult>({
        status: "idle",
        parts: null,
    });

    const resourceId = target?.resourceId;
    const segmentId = target?.segmentId ?? "";
    const sectionId = target?.sectionId;
    const hasTarget = target !== null;

    useEffect(() => {
        if (!resourceId) {
            // No topic id anywhere. Do not call the API without one.
            setResult({
                status: hasTarget ? "unavailable" : "idle",
                parts: null,
            });
            return;
        }

        let cancelled = false;
        setResult({status: "loading", parts: null});

        const load = async () => {
            if (sectionId) {
                const withSection = await trigger(
                    {resourceId, segmentId, id: sectionId},
                    PREFER_CACHE
                );

                if (cancelled) return;
                if (withSection.error) {
                    if (isAccessDenied(withSection.error)) {
                        setResult({status: "denied", parts: null});
                        return;
                    }
                } else if ((withSection.data?.parts.length ?? 0) > 0) {
                    setResult({status: "loaded", parts: withSection.data!.parts});
                    return;
                }
            }

            const atSegment = await trigger(
                {resourceId, segmentId, id: ""},
                PREFER_CACHE
            );
            if (cancelled) return;

            if (atSegment.error) {
                setResult({
                    status: isAccessDenied(atSegment.error) ? "denied" : "unavailable",
                    parts: null,
                });
                return;
            }

            if ((atSegment.data?.parts.length ?? 0) > 0) {
                setResult({status: "loaded", parts: atSegment.data!.parts});
            } else {
                setResult({status: "unavailable", parts: null});
            }
        };

        load().catch(() => {
            if (!cancelled) setResult({status: "unavailable", parts: null});
        });

        return () => {
            cancelled = true;
        };
    }, [resourceId, segmentId, sectionId, hasTarget, trigger]);

    return result;
}
